(() => {
  const instances = new WeakMap();
  function init(root) {
    if (instances.has(root)) return;
    const header = document.getElementById(root.dataset.headerId);
    if (!header) return;
    const controller = new AbortController();
    const signal = controller.signal;
    const parent = root.parentNode;
    const sibling = root.nextSibling;
    const mobile = matchMedia('(max-width: 989px)');
    const hover = matchMedia('(hover: hover) and (pointer: fine)');
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    const items = [...root.querySelectorAll('[data-qnav-item]')];
    const motions = new Map();
    const opener = header.querySelector('[data-wubs-mm-open]');
    const sheet = root.querySelector('.qnav__sheet');
    let mobileOpen = false;
    let savedScroll = 0;
    let savedBody = null;
    let hoverTimer = 0;
    let raf = 0;
    function position() {
      const bottom = `${Math.round(header.getBoundingClientRect().bottom)}px`;
      root.style.setProperty('--qnav-bottom', bottom);
    }
    function layout() {
      header.classList.remove('qnav-header--stacked');
      if (matchMedia('(min-width: 1400px)').matches) {
        const list = root.querySelector('.qnav__list');
        if (list.scrollWidth > root.clientWidth + 1) header.classList.add('qnav-header--stacked');
      }
      position();
    }
    function syncItems() {
      items.forEach(item => item.querySelector('summary').setAttribute('aria-expanded', String(item.open && !item.dataset.qnavClosing)));
      root.classList.toggle('qnav--open', items.some(item => item.open && !item.dataset.qnavClosing));
    }
    function stopMotion(item) {
      motions.get(item)?.cancel();
      motions.delete(item);
    }
    function animatePanel(item, frames, duration, finish) {
      const panel = item.querySelector('.qnav__panel');
      const animation = panel.animate(frames, { duration, easing: 'cubic-bezier(.2, .7, .2, 1)' });
      motions.set(item, animation);
      animation.finished.then(() => {
        if (motions.get(item) !== animation || signal.aborted) return;
        motions.delete(item);
        finish?.();
      }, () => {});
    }
    function closeItem(item, immediate) {
      if (item.dataset.qnavClosing && !immediate && !mobile.matches && !reducedMotion.matches) return;
      const panel = item.querySelector('.qnav__panel');
      const style = getComputedStyle(panel);
      const from = { opacity: style.opacity, transform: style.transform };
      stopMotion(item);
      const finish = () => {
        item.open = false;
        delete item.dataset.qnavClosing;
        panel.inert = false;
        syncItems();
      };
      if (!item.open || immediate || mobile.matches || reducedMotion.matches || !panel.animate) { finish(); return; }
      item.dataset.qnavClosing = 'true';
      panel.inert = true;
      animatePanel(item, [from, { opacity: 0, transform: 'translateY(-8px)' }], 180, finish);
    }
    function closeItems(except, immediate = false) {
      items.forEach(item => {
        if (item !== except && item.open) closeItem(item, immediate || Boolean(except));
      });
      syncItems();
    }
    function openItem(item) {
      const panel = item.querySelector('.qnav__panel');
      const closing = Boolean(item.dataset.qnavClosing);
      const alreadyOpen = item.open && !closing;
      const style = getComputedStyle(panel);
      const from = closing ? { opacity: style.opacity, transform: style.transform } : { opacity: 0, transform: 'translateY(-8px)' };
      if (!alreadyOpen) stopMotion(item);
      delete item.dataset.qnavClosing;
      item.open = true;
      panel.inert = false;
      closeItems(item, true);
      position();
      if (!alreadyOpen && !mobile.matches && !reducedMotion.matches && panel.animate) {
        animatePanel(item, [from, { opacity: 1, transform: 'translateY(0)' }], 220);
      }
    }
    function lock() {
      savedScroll = scrollY;
      savedBody = { position: document.body.style.position, top: document.body.style.top, width: document.body.style.width };
      document.body.style.position = 'fixed';
      document.body.style.top = `-${savedScroll}px`;
      document.body.style.width = '100%';
      document.body.classList.add('wubs-mm-open');
    }
    function unlock() {
      if (!savedBody) return;
      Object.assign(document.body.style, savedBody);
      savedBody = null;
      document.body.classList.remove('wubs-mm-open');
      const behavior = document.documentElement.style.scrollBehavior;
      document.documentElement.style.scrollBehavior = 'auto';
      scrollTo({ top: savedScroll, behavior: 'instant' });
      document.documentElement.style.scrollBehavior = behavior;
    }
    function closeMobile(restore = true) {
      mobileOpen = false;
      root.dataset.mobileOpen = 'false';
      root.removeAttribute('role');
      root.removeAttribute('aria-modal');
      root.removeAttribute('aria-labelledby');
      opener?.setAttribute('aria-expanded', 'false');
      closeItems(undefined, true);
      unlock();
      if (parent.isConnected) parent.insertBefore(root, sibling?.parentNode === parent ? sibling : null);
      root.inert = mobile.matches;
      if (restore && opener && mobile.matches) opener.focus({ preventScroll: true });
    }
    function openMobile() {
      if (!mobile.matches) return;
      header.querySelectorAll('[data-wubs-account-panel]').forEach(panel => { panel.hidden = true; });
      header.querySelectorAll('[data-wubs-account-menu]').forEach(menu => menu.classList.remove('is-open'));
      header.querySelectorAll('[data-wubs-account-trigger]').forEach(trigger => trigger.setAttribute('aria-expanded', 'false'));
      closeItems();
      position();
      document.body.appendChild(root);
      lock();
      mobileOpen = true;
      root.inert = false;
      root.dataset.mobileOpen = 'true';
      root.setAttribute('role', 'dialog');
      root.setAttribute('aria-modal', 'true');
      root.setAttribute('aria-labelledby', `qnav-title-${header.id.replace('wubs-header-', '')}`);
      opener?.setAttribute('aria-expanded', 'true');
      sheet.scrollTop = 0;
      root.querySelector('.qnav__close').focus({ preventScroll: true });
    }
    root.dataset.enhanced = 'true';
    root.dataset.mobileOpen = 'false';
    root.inert = mobile.matches;
    opener?.addEventListener('click', () => mobileOpen ? closeMobile() : openMobile(), { signal });
    root.querySelectorAll('[data-qnav-dismiss]').forEach(button => button.addEventListener('click', () => {
      if (mobileOpen) closeMobile();
      else {
        const active = items.find(item => item.open)?.querySelector('summary');
        closeItems();
        active?.focus({ preventScroll: true });
      }
    }, { signal }));
    items.forEach(item => {
      const trigger = item.querySelector('summary');
      trigger.setAttribute('aria-expanded', 'false');
      item.addEventListener('toggle', () => {
        if (item.dataset.qnavClosing) return;
        if (item.open) { closeItems(item); position(); }
        syncItems();
      }, { signal });
      trigger.addEventListener('click', event => {
        event.preventDefault();
        clearTimeout(hoverTimer);
        if (item.open && !item.dataset.qnavClosing) closeItems();
        else openItem(item);
      }, { signal });
      trigger.addEventListener('keydown', event => {
        if (event.key !== 'ArrowDown') return;
        event.preventDefault();
        openItem(item);
        item.querySelector('.qnav__panel a')?.focus();
      }, { signal });
      item.addEventListener('pointerenter', () => {
        clearTimeout(hoverTimer);
        if (mobile.matches || !hover.matches) return;
        hoverTimer = setTimeout(() => openItem(item), 160);
      }, { signal });
      item.addEventListener('pointerleave', () => {
        clearTimeout(hoverTimer);
        if (mobile.matches || !hover.matches) return;
        hoverTimer = setTimeout(() => { if (!item.contains(document.activeElement)) closeItems(); }, 240);
      }, { signal });
    });
    document.addEventListener('pointerdown', event => {
      if (!root.contains(event.target) && !opener?.contains(event.target)) {
        clearTimeout(hoverTimer);
        closeItems();
        if (mobileOpen) closeMobile(false);
      }
    }, { signal, capture: true });
    document.addEventListener('keydown', event => {
      if (event.key === 'Escape') {
        clearTimeout(hoverTimer);
        if (mobileOpen) { event.preventDefault(); closeMobile(); return; }
        const active = items.find(item => item.open)?.querySelector('summary');
        if (active) { event.preventDefault(); closeItems(); active.focus({ preventScroll: true }); }
      }
      if (event.key !== 'Tab' || !mobileOpen) return;
      const focusable = [...root.querySelectorAll('a[href],button:not([disabled]),summary,[tabindex="0"]')].filter(el => el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden');
      const first = focusable[0], last = focusable.at(-1);
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }, { signal });
    root.addEventListener('focusout', () => {
      requestAnimationFrame(() => { if (!mobileOpen && !root.contains(document.activeElement)) closeItems(); });
    }, { signal });
    root.addEventListener('click', event => {
      if (event.target.closest('a[href]') && mobileOpen) closeMobile(false);
    }, { signal });
    header.addEventListener('click', event => {
      if (event.target.closest('.wubs-h__searchBtn,[data-wubs-cart-trigger],[data-wubs-account-trigger]')) {
        clearTimeout(hoverTimer);
        closeItems();
        if (mobileOpen) closeMobile(false);
      }
    }, { signal, capture: true });
    mobile.addEventListener('change', () => {
      clearTimeout(hoverTimer);
      closeMobile(false);
      root.inert = mobile.matches;
      position();
    }, { signal });
    reducedMotion.addEventListener('change', () => {
      if (reducedMotion.matches) motions.forEach(animation => animation.finish());
    }, { signal });
    window.addEventListener('resize', layout, { signal, passive: true });
    window.addEventListener('scroll', () => {
      if (raf || mobile.matches || !root.classList.contains('qnav--open')) return;
      raf = requestAnimationFrame(() => { position(); raf = 0; });
    }, { signal, passive: true });
    document.addEventListener('shopify:block:select', event => {
      const entry = event.target.closest('.qnav__entry');
      if (!entry || !root.contains(entry)) return;
      if (mobile.matches) openMobile();
      const item = entry.querySelector('details');
      openItem(item);
    }, { signal });
    layout();
    document.fonts.ready.then(() => { if (!signal.aborted) layout(); });
    instances.set(root, () => {
      clearTimeout(hoverTimer);
      cancelAnimationFrame(raf);
      closeMobile(false);
      controller.abort();
      instances.delete(root);
    });
  }
  const load = () => document.querySelectorAll('[data-qnav]').forEach(init);
  load();
  document.addEventListener('shopify:section:load', load);
  document.addEventListener('shopify:section:unload', event => {
    document.querySelectorAll('[data-qnav]').forEach(root => {
      if (event.target.contains(root) || event.target.querySelector(`#${CSS.escape(root.dataset.headerId)}`)) instances.get(root)?.();
    });
  });
})();
